
























































































import { ButtonSize, ButtonVariant } from '~/models/app/button'
import { defineComponent, computed, PropType } from '~/utils/nuxt3-migration'
import CLink from '~/components/shared/configurable/link/CLink.vue'
import { buttonVariantConfigs } from '~/constants/button'
import { useGlobalElements } from '~/compositions/global-elements'

export default defineComponent({
  props: {
    size: {
      type: String as PropType<ButtonSize>,
      default: 'md'
    },
    variant: {
      type: String as PropType<ButtonVariant>,
      default: undefined
    },
    outline: { type: Boolean, default: false },
    tertiary: { type: Boolean, default: false },
    circle: { type: Boolean, default: false },
    pill: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    pressed: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    tag: { type: String, default: 'button' },
    href: { type: String, default: null },
    to: { type: [Object, String], default: null },
    target: { type: String, default: '_self' },
    title: { type: String, default: null },
    loading: { type: Boolean, default: false },
    inGroup: { type: Boolean, default: false },
    icon: { type: Object, default: () => null },
    iconPosition: {
      type: String as PropType<'left' | 'right'>,
      default: 'left',
      required: false
    },
    spinnerVariantProp: {
      type: String as PropType<ButtonVariant>,
      default: undefined
    },
    floating: { type: Boolean, default: false },
    disableHover: { type: Boolean, default: false },
    containerClasses: { type: String, required: false, default: '' },
    slotClasses: {
      type: [Object, String, Array],
      required: false,
      default: ''
    },
    attributeValue: { type: String, default: undefined },
    type: { type: String, default: 'button' },
    iconContainerClasses: {
      type: String,
      default: ''
    },
    truncate: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit, slots }) {
    const isLink = computed(() => Boolean(props.href || props.to))

    const { showMobileBottomMenu } = useGlobalElements()

    const isIconOnly = computed(() => {
      const hasIcon = props.icon || slots.icon

      return hasIcon && !slots.default
    })

    const spinnerClasses = computed(() => {
      if (props.floating) {
        return ['tw--left-0.5']
      }
      if (props.size !== 'xs') {
        return ['tw--left-2']
      }
      return []
    })

    const sizeClasses = computed(() => {
      if (props.floating) {
        return [
          'tw-w-14',
          'tw-h-14',
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-text-3xl',
          'tw-shadow'
        ]
      }
      if (props.circle) {
        return [
          'tw-w-10',
          'tw-h-10',
          'tw-text-lg',
          'tw-flex',
          'tw-items-center',
          'tw-justify-center'
        ]
      }

      if (props.size === 'xs') {
        return [
          props.inGroup
            ? 'tw-h-[1.375] tw-min-h-[1.375]'
            : 'tw-h-[1.5rem] tw-min-h-[1.5rem]',
          isIconOnly.value ? 'tw-px-[5px]' : 'tw-px-1',
          'tw-py-0.5',
          'tw-text-sm',
          'tw-rounded',
          'tw-font-medium'
        ]
      }

      if (props.size === 'sm') {
        return [
          props.inGroup
            ? 'tw-h-[1.875rem] tw-min-h-[1.875rem]'
            : 'tw-h-[2rem] tw-min-h-[2rem]',
          isIconOnly.value ? 'tw-px-[7px]' : 'tw-px-2',
          'tw-py-0',
          'tw-text-base',
          'tw-rounded-md',
          'tw-font-medium'
        ]
      }

      if (props.size === 'lg') {
        return [
          props.inGroup
            ? 'tw-h-[2.875rem] tw-min-h-[2.875rem]'
            : 'tw-h-[3rem] tw-min-h-[3rem]',
          isIconOnly.value ? 'tw-px-[11px]' : 'tw-px-4',
          'tw-py-0',
          'tw-text-lg',
          'tw-rounded-xl',
          'tw-font-medium'
        ]
      }

      return [
        props.inGroup
          ? 'tw-h-[2.375rem] tw-min-h-[2.375rem]'
          : 'tw-h-[2.5rem] tw-min-h-[2.5rem]',
        isIconOnly.value ? 'tw-px-[9px]' : 'tw-px-3',
        'tw-py-0',
        'tw-text-base',
        'tw-rounded-lg',
        'tw-font-medium'
      ]
    })

    const componentType = computed(() => {
      if (isLink.value) {
        return CLink
      }
      return props.tag
    })

    const role = computed(() => (isLink.value ? 'button' : undefined))

    const spinnerVariant = computed(() => {
      if (props.spinnerVariantProp) {
        return props.spinnerVariantProp
      }

      if (props.outline || props.tertiary) {
        return props.variant
      }

      if (props.variant === 'light') {
        return 'light'
      }

      return 'white'
    })

    const spinnerSize = computed(() => {
      switch (props.size) {
        case 'md':
        default:
          return { size: 'tw-w-[20px] tw-h-[20px]', border: 'tw-border-4' }
        case 'xs':
          return { size: 'tw-w-[12px] tw-h-[12px]', border: 'tw-border-2' }
        case 'sm':
          return { size: 'tw-w-[16px] tw-h-[16px]', border: 'tw-border-2' }
        case 'lg':
          return { size: 'tw-w-[24px] tw-h-[24px]', border: 'tw-border-4' }
      }
    })

    const iconSizeClass = computed(() => {
      switch (props.size) {
        case 'md':
        default:
          return 'tw-text-[20px]'
        case 'xs':
          return 'tw-text-[12px]'
        case 'sm':
          return 'tw-text-[16px]'
        case 'lg':
          return 'tw-text-[24px]'
      }
    })

    const variantClasses = computed(() => {
      if (props.disabled && !props.loading) {
        return [
          'tw-bg-grey-300',
          '!tw-text-grey-600',
          '!tw-cursor-not-allowed',
          'tw-border',
          'tw-border-solid',
          'tw-border-grey-300'
        ]
      }

      const config =
        buttonVariantConfigs[props.variant] || buttonVariantConfigs.primary

      let variantConfig
      if (props.tertiary && config.tertiary) {
        variantConfig = config.tertiary
      } else if (props.outline && config.outline) {
        variantConfig = config.outline
      } else {
        variantConfig = config.solid
      }

      const loadingClasses = props.loading
        ? ['disabled:tw-opacity-70', 'tw-cursor-wait']
        : []

      const focusClasses =
        !props.loading && !props.disableHover ? variantConfig.focus : []

      const hoverClasses =
        !props.loading && !props.disableHover ? variantConfig.hover : []

      const activeClasses =
        !props.loading && !props.disableHover ? variantConfig.active : []

      const pressedClasses =
        props.pressed && !props.loading ? variantConfig.pressed : []

      return [
        ...variantConfig.base,
        ...pressedClasses,
        ...hoverClasses,
        ...loadingClasses,
        ...activeClasses,
        ...focusClasses
      ]
    })

    const linkClasses = computed(() =>
      isLink.value ? 'tw-inline-block tw-cursor-pointer' : null
    )

    const positionClasses = computed(() => {
      if (props.floating) {
        return [
          'tw-fixed',
          'tw-right-4',
          showMobileBottomMenu.value
            ? 'lg:tw-bottom-4 tw-bottom-16'
            : 'tw-bottom-4',
          'floating-button'
        ]
      }

      return ['tw-relative']
    })

    const roundnessClasses = computed(() => {
      if (props.floating || props.circle) {
        return ['tw-rounded-full']
      } else if (props.pill) {
        return ['!tw-rounded-[50rem]']
      }
      // else it will be handled by the sizeClasses
      return []
    })

    function handleClick(e: Event) {
      if (!props.disabled) {
        emit('click', e)
      }
    }

    return {
      componentType,
      variantClasses,
      sizeClasses,
      linkClasses,
      role,
      isLink,
      spinnerSize,
      spinnerVariant,
      positionClasses,
      roundnessClasses,
      spinnerClasses,
      isIconOnly,
      iconSizeClass,
      handleClick
    }
  }
})
